import React from 'react';

function PageCard({ title, children }) {
  return (
    <div className="calc-card">
      <div className="calc-header" style={{ background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' }}>
        <h2>{title}</h2>
      </div>
      <div className="calc-body static-page">{children}</div>
    </div>
  );
}

export function AboutPage() {
  return (
    <PageCard title="About PocketCalculator.net">
      <p>PocketCalculator.net is a free online destination for anyone who needs a fast, reliable answer to an everyday numeric question. Our library covers <b>200+ calculators</b> across Math, Financial, Fitness &amp; Health, and utility categories — from mortgage amortization and BMI to fraction arithmetic and IP subnet planning.</p>
      <h3>Our Mission</h3>
      <p>Bring pixel-perfect, printable, and mobile-first calculators to students, teachers, engineers, homeowners, athletes, and small-business owners — no signup, no ads-first paywalls, no jargon.</p>
      <h3>What we build</h3>
      <ul>
        <li><b>Step-by-step working</b> so users learn the method, not just the answer.</li>
        <li><b>Diagrams &amp; charts</b> for geometry, statistics, and finance visuals.</li>
        <li><b>Unit-safe inputs</b> — every field shows its unit and converts internally.</li>
        <li><b>Print-ready results</b> with three modes: result only / inputs + result / everything.</li>
      </ul>
      <p>Have feedback, feature requests, or spotted a calculation bug? Please write to <a href="mailto:saurabhdn99@gmail.com">saurabhdn99@gmail.com</a>. We answer every message.</p>
    </PageCard>
  );
}

export function ContactPage() {
  return (
    <PageCard title="Contact Us">
      <p>We would love to hear from you. Whether you have found a bug, would like a new calculator, or want to share how you use the site, drop us a line.</p>
      <div className="p-5 bg-blue-50 rounded-xl border border-blue-100 my-4">
        <div className="font-bold text-slate-900">Email us</div>
        <div className="text-lg"><a href="mailto:saurabhdn99@gmail.com" className="text-blue-700 hover:underline">saurabhdn99@gmail.com</a></div>
        <div className="text-sm text-slate-600 mt-1">We typically reply within 1–2 business days.</div>
      </div>
      <h3>What to include in your message</h3>
      <ul>
        <li>Name of the calculator you were using (or the URL like <code>/calc/mortgage</code>).</li>
        <li>The inputs you entered.</li>
        <li>What you expected vs what you saw.</li>
        <li>A screenshot if the issue is visual.</li>
      </ul>
      <h3>Business, partnership or ads</h3>
      <p>For advertising, sponsorships, or partnership enquiries, please email <a href="mailto:saurabhdn99@gmail.com">saurabhdn99@gmail.com</a> with "Partnership" in the subject line.</p>
    </PageCard>
  );
}

export function TermsPage() {
  return (
    <PageCard title="Terms & Conditions">
      <p>Last updated: {new Date().toLocaleDateString()}</p>
      <h3>1. Use of the Service</h3>
      <p>PocketCalculator.net ("we", "us") provides free online calculators for educational and informational purposes only. By using the site you agree to these Terms.</p>
      <h3>2. No Professional Advice</h3>
      <p>Results are computational estimates. They do not constitute financial, medical, engineering, legal, or tax advice. Always consult a qualified professional for critical decisions.</p>
      <h3>3. Accuracy</h3>
      <p>We work hard to keep formulas and constants correct, but we make no warranty about the accuracy or completeness of any result. Use the site at your own risk.</p>
      <h3>4. Intellectual Property</h3>
      <p>All original content, layouts, code, and calculator visuals are the property of PocketCalculator.net. You may share links freely; you may not scrape, redistribute or resell our content without written permission.</p>
      <h3>5. Third-Party Services</h3>
      <p>The site uses Google AdSense and Google Analytics. Their respective privacy policies apply. See our <a href="/privacy">Privacy Policy</a> for details.</p>
      <h3>6. Changes to the Terms</h3>
      <p>We may update these Terms from time to time. Continued use after changes constitutes acceptance.</p>
      <h3>7. Contact</h3>
      <p>Questions about these Terms? Write to <a href="mailto:saurabhdn99@gmail.com">saurabhdn99@gmail.com</a>.</p>
    </PageCard>
  );
}

export function PrivacyPage() {
  return (
    <PageCard title="Privacy Policy">
      <p>Last updated: {new Date().toLocaleDateString()}</p>
      <h3>1. Information we collect</h3>
      <p>PocketCalculator.net does <b>not</b> ask you to sign up or provide personal information. The calculations you run are performed in your browser. However, third-party services listed below record some information about how you interact with the site.</p>
      <h3>2. Cookies and Analytics</h3>
      <p>We use <b>Google Analytics 4</b> (measurement ID G-03RMTR72B9) to understand aggregate traffic — page views, browsers, geography, referrers. This uses first-party cookies. You can opt out with any standard browser cookie-blocking extension.</p>
      <h3>3. Session Recording (Product Analytics)</h3>
      <p>The site runs <b>PostHog</b> session-recording (via <code>ap.emergent.sh</code>) for product-quality debugging. This captures your clicks, scrolls, and the values you type into calculator input fields, and stores the recording for a limited retention window. Recordings do not include your keystrokes on password or credit-card style fields (there aren't any on this site). If you prefer not to be recorded, use your browser's Do Not Track / Global Privacy Control setting or an ad-blocker that blocks <code>*.posthog.com</code> / <code>ap.emergent.sh</code>.</p>
      <h3>4. Third-party APIs used by specific calculators</h3>
      <p>A few calculators call external services to look up data. When you use those calculators, the values you enter are sent to the corresponding third party:</p>
      <ul>
        <li><b>Mileage / Distance Between Cities Calculator</b> — sends the city / address strings you type to <b>OpenStreetMap Nominatim</b> (<code>nominatim.openstreetmap.org</code>) for geocoding and to <b>Project OSRM</b> (<code>router.project-osrm.org</code>) for routing.</li>
        <li><b>Currency-conversion utilities</b> — fetch daily exchange rates from a public currency API (no user input sent).</li>
      </ul>
      <h3>5. Advertising</h3>
      <p>Google AdSense (publisher ID <code>ca-pub-3584823300252981</code>) serves ads on this site. Google may use cookies to personalise ads based on your prior visits. You can opt out of personalised advertising at <a href="https://www.google.com/settings/ads" target="_blank" rel="noreferrer noopener">google.com/settings/ads</a>. EU/UK visitors: your ad choices are governed by our advertising partners' consent mechanisms.</p>
      <h3>6. Third-Party Vendors</h3>
      <p>Third parties, including Google and PostHog, use cookies to serve ads and record product analytics based on your visits to this and other sites. Their use of these cookies enables them to deliver relevant advertisements and improve the product.</p>
      <h3>7. Children</h3>
      <p>The site is safe for all ages. We do not knowingly collect information from children under 13.</p>
      <h3>8. Your Rights</h3>
      <p>You may email us at <a href="mailto:saurabhdn99@gmail.com">saurabhdn99@gmail.com</a> at any time to request deletion of any recorded session data associated with your visit. Please include the approximate date &amp; time and, if possible, the IP address you used, so we can locate the recording.</p>
      <h3>9. Changes</h3>
      <p>We may update this Privacy Policy at any time. Continued use of the site indicates acceptance of the updated policy.</p>
    </PageCard>
  );
}
